/** @jsxImportSource react */
import { abbreviatePriceWithOptions } from '@common/helpers/abbreviatePrice';
import { ADL_OPTIONS_MAP } from '@react/gwizzy/constants';
import { usePrefetch } from '@react/services/api';
import PricingByRoomSize from '@react/widgets/pricingCalculator/components/PricingByRoomSize';
import PricingCalculator from '@react/widgets/pricingCalculator/components/PricingCalculator';
import { tocToCareLevelMap } from '@react/widgets/pricingCalculator/constants';
import {
  getCareLevelFromToc,
  getFiltersFromFormValues,
  getPricingFromGeoGuides,
} from '@react/widgets/pricingCalculator/helpers';
import { TOCS } from '@sly/core/constants/filters';
import { useEffect, useMemo, useState } from 'react';

const PricingCalculatorContainer = ({
  initialOptions = {},
  onCtaClick = (_message, _careLevel, _budget) => {},
  onSubmit = (_filters) => {},
  hasCta = true,
  submitButtonClassName = '',
  submitButtonVariant = 'outlined',
  eventProps = {},
}) => {
  const [filters, setFilters] = useState(() => {
    if (initialOptions?.city && initialOptions?.state && initialOptions?.toc) {
      return {
        city: initialOptions.city,
        state: initialOptions.state,
        toc: Object.keys(tocToCareLevelMap).includes(initialOptions.toc)
          ? initialOptions.toc
          : 'independent-living',
      };
    }
  });
  const [roomType, setRoomType] = useState(() => initialOptions?.roomType);
  const [locationText, setLocationText] = useState(
    () => initialOptions?.locationString
  );
  const [pricingData, setPricingData] = useState(null);
  const [mobility, setMobility] = useState(
    () => initialOptions?.mobility || 'no-issues'
  );
  // handle care level
  const [careLevel, setCareLevel] = useState(
    getCareLevelFromToc(initialOptions?.toc)
  );

  const {
    requestInfo: { normalized, hasFinished, isLoading, hasStarted },
  } = usePrefetch(
    'getGeoGuides',
    { ...filters, include: 'curatedCommunities' },
    {
      encode: false,
      shouldBail: !filters,
    }
  );

  useEffect(() => {
    if (hasFinished) {
      const filterdPricing = getPricingFromGeoGuides({
        roomType,
        mobility,
        careLevel,
        geoGuidesData: normalized,
      });
      setPricingData(filterdPricing);
    }
  }, [hasFinished, normalized, filters, roomType, mobility, careLevel]);

  const handleSubmit = (values) => {
    const currentFilters = getFiltersFromFormValues(values);
    const locationString = values?.location?.displayText;

    setCareLevel(values?.careLevel);

    if (locationString || locationText) {
      const useLocationFallback = !locationString && locationText;

      setLocationText(locationString || locationText);

      if (useLocationFallback) {
        values.location.displayText = locationText;
      }
    }
    setFilters(currentFilters);
    setRoomType(values?.typeOfRoom);
    setMobility(values?.mobility);
    if (onSubmit) {
      onSubmit(values);
    }
  };

  const initialFormValue = useMemo(() => {
    if (initialOptions) {
      return {
        initialFilters: {
          careLevel: getCareLevelFromToc(initialOptions?.toc),
          typeOfRoom: initialOptions?.roomType,
          location: {
            searchParams: {
              state: initialOptions?.state,
              city: initialOptions?.city,
            },
          },
          mobility: initialOptions?.mobility || 'no-issues',
        },
        locationString: initialOptions?.locationString,
      };
    } else {
      return {};
    }
  }, [initialOptions]);

  const handleCtaClick = () => {
    const toc = TOCS?.[filters?.toc]?.label?.toLowerCase() || '';

    let minPrice = 0;

    if (pricingData) {
      minPrice = Math.min(...pricingData.values());
    }

    const careNeeds = Object.values(ADL_OPTIONS_MAP).find(
      ({ value }) => value === careLevel
    );

    let msg = `I'd like a list of ${toc} communities in ${locationText}. I am mainly interested in ${roomType} rooms.`;

    if (careNeeds) {
      msg += ` I need assistance with ${careNeeds.label}.`;
    }

    if (mobility && mobility !== 'no-issues') {
      msg += ` I also use ${mobility} for moving.`;
    }

    if (minPrice && minPrice !== Infinity) {
      msg += ` I am comfortable with price around ${abbreviatePriceWithOptions(
        minPrice,
        { notation: 'standard' }
      )}.`;
    }

    msg += ` Thank you!`;

    if (onCtaClick) {
      onCtaClick(msg, careLevel, minPrice);
    }
  };

  return (
    <article className='grid gap-4'>
      <PricingCalculator
        handleSubmit={handleSubmit}
        submitButtonVariant={submitButtonVariant}
        submitButtonClassName={submitButtonClassName}
        eventProps={eventProps}
        {...initialFormValue}
      />
      {(pricingData || isLoading || hasStarted || hasFinished) && (
        <div className='grid items-center rounded-lg bg-viridian-lighter-90 p-6'>
          {pricingData ? (
            <PricingByRoomSize
              pricingData={pricingData}
              filters={filters}
              locationText={locationText}
              onCtaClick={hasCta ? handleCtaClick : null}
            />
          ) : isLoading ? (
            <div className='justify-self-center' role='status'>
              <svg
                aria-hidden='true'
                className='mr-2 h-8 w-8 animate-spin fill-blue-600 text-gray-200 dark:text-gray-600'
                viewBox='0 0 100 101'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z'
                  fill='currentColor'
                />
                <path
                  d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z'
                  fill='currentFill'
                />
              </svg>
              <span className='sr-only'>Loading...</span>
            </div>
          ) : hasStarted && hasFinished && !pricingData?.length ? (
            <div className='justify-self-center text-center'>
              Sorry, we don&#39;t have the data for the values you selected. Try
              selecting a different area.
            </div>
          ) : null}
        </div>
      )}
    </article>
  );
};

export default PricingCalculatorContainer;
